import { Component, HostListener, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DirtyCheck } from 'src/app/common/guards/form-deactivate.guard';
import { UserService } from '../user.service';
import { UserModel } from '../model/user.model';

import { UserGroupMenuModel } from '../../usergroup/model/usergroupmenu.model';
import e from 'express';
import { UserGroupService } from '../../usergroup/usergroup.service';
import { EmployeeService } from '../../../employees/employee/employee.service';
import { EmployeeModel } from '../../../employees/employee/model/employee.model';
import { Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import * as CryptoJS from 'crypto-js';
import { PermissionService } from 'src/app/auth/permission.service';

@Component({
  selector: 'app-user-edit',
  standalone: false,
  templateUrl: './user-edit.component.html',
  styleUrl: './user-edit.component.scss'
})
export class UserEditComponent {
  title: string = "New User ";
  editForm: FormGroup;
  userInfo: UserModel = {};
  userGroupMenuInfo: UserGroupMenuModel = {};
  isFormDirty: boolean = false;
  userGroupList: UserModel[] = [];
  employeeList: EmployeeModel[] = [];
  shopUserMapping: any[] = [];
  sortedShopUserMapping: any[] = [];
  shopUserMappingLoadedFromSetUserGroupInfo = false;
  areaList: string[] = [];
  selectedAreas: string[] = [];

  shopSearch: string = '';
  permission: any;
  filteredUserGroups!: Observable<any[]>;
  filteredEmployees!: Observable<any[]>;

  private userGroupList$ = new BehaviorSubject<any[]>([]);
  private employeeList$ = new BehaviorSubject<any[]>([]);
  userId: string | null;

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private route: ActivatedRoute,
    private userService: UserService,
    private userGroupService: UserGroupService,
    private employeeService: EmployeeService,
    private snackBarService: SnackBarService,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private permissionService: PermissionService,
    private zone: NgZone
  ) {

    this.userId = this.route.snapshot.paramMap.get('id');

    this.editForm = this.builder.group({
      code: this.builder.control(this.userInfo.code, [Validators.required, Validators.maxLength(10)]),
      name: this.builder.control(this.userInfo.name, [Validators.required, Validators.maxLength(50)]),
      card_no: this.builder.control(this.userInfo.card_no, [Validators.required]),
      email: this.builder.control(this.userInfo.email, [Validators.email, Validators.maxLength(100)]),
      valid_from: this.builder.control(this.userInfo.valid_from),
      valid_to: this.builder.control(this.userInfo.valid_to),
      user_group_id: this.builder.control(this.userInfo.user_group_id, [Validators.required]),
      employee_id: this.builder.control(this.userInfo.employee_id),
      is_active: this.builder.control(
        this.toBoolean(this.userInfo.is_active), // use helper
      ),

      user_type: this.builder.group({
        hq: [false],
        shop: [false],
        areaManager: [false],
      }),
      password: this.builder.control('', this.userId === null || parseInt(this.userId) === 0 ? [Validators.required] : []),
      confirm_password: this.builder.control('', this.userId === null || parseInt(this.userId) === 0 ? [Validators.required] : []),
    }, { validators: [this.dateRangeValidator, this.passwordMatchValidator] });

    this.editForm.valueChanges.subscribe(() => {
      this.isFormDirty = true;
    });

    if (this.userId !== null && parseInt(this.userId) !== 0) {
      this.setUserInfo(this.userId);
    }
    this.setupUserTypeLogic();
    this.permission = this.permissionService.getRowPermission(this.permissionService.getPagePermission("user"), this.userId);

  }
  ngOnInit(): void {
    this.loadUserGroupData();
    this.loadEmployeeData();

    // Listen to changes on employee selection to handle card no disabled state
    this.editForm.get('employee_id')?.valueChanges.subscribe(employeeId => {
      this.updateCardNoFromEmployee(employeeId);
    });
    this.editForm.get('user_group_id')?.valueChanges.subscribe(userGroupId => {
      // Only call getShopUserMapping if setuserGroupInfo did NOT already call it
      if (!this.shopUserMappingLoadedFromSetUserGroupInfo) {
        if (this.userId) {
          this.userService.getShopUserMapping(Number(this.userId)).subscribe(res => {
            this.zone.run(() => {
              if (res.status === 'SUCCESS') {
                this.shopUserMapping = [...res.data];
                // Extract unique area/city list
                this.areaList = Array.from(new Set(this.shopUserMapping.map(shop => shop.area || 'No Area'))).filter(Boolean);
                // By default, select all areas
                this.selectedAreas = [...this.areaList];
                this.sortedShopUserMapping = this.getFilteredAndSortedShopUserMapping();
              } else {
                this.shopUserMapping = [];
                this.areaList = [];
                this.selectedAreas = [];
                this.sortedShopUserMapping = [];
              }
              console.log('shopUserMapping after load:', this.shopUserMapping);
              this.cdr.detectChanges();
            });
          });
        } else {
          this.zone.run(() => {
            this.shopUserMapping = [];
            this.sortedShopUserMapping = [];
            this.cdr.detectChanges();
          });
        }
      }
      // Reset the flag for subsequent changes
      this.shopUserMappingLoadedFromSetUserGroupInfo = false;
    });

    this.filteredUserGroups = combineLatest([
      this.editForm.get('user_group_id')!.valueChanges.pipe(startWith(this.editForm.get('user_group_id')?.value || '')),
      this.userGroupList$
    ]).pipe(
      map(([value, list]) => this._filterUserGroups(value, list))
    );

    this.filteredEmployees = combineLatest([
      this.editForm.get('employee_id')!.valueChanges.pipe(startWith(this.editForm.get('employee_id')?.value || '')),
      this.employeeList$
    ]).pipe(
      map(([value, list]) => this._filterEmployees(value, list))
    );
  }

  private _filterUserGroups(value: any, list: any[]): any[] {
    // If value is a number, it means an item is selected; return the full list
    if (typeof value === 'number') return list;
    const filterValue = (value || '').toString().toLowerCase();
    return list.filter(group => (group.name || '').toLowerCase().includes(filterValue));
  }

  displayUserGroup = (groupId: any): string => {
    if (!this.userGroupList) return '';
    const found = this.userGroupList.find(g => g.id === groupId || g.id == groupId);
    return found?.name || '';
  }

  onUserGroupSelected(event: any) {
    const selectedId = event.option.value;
    this.editForm.get('user_group_id')?.setValue(selectedId);
  }

  clearUserGroup() {
    this.editForm.get('user_group_id')?.setValue(null);
  }

  private _filterEmployees(value: any, list: any[]): any[] {
    // If value is a number, it means an item is selected; return the full list
    if (typeof value === 'number') return list;
    const filterValue = (value || '').toString().toLowerCase();
    return list.filter(emp =>
      this.displayEmployee(emp.id).toLowerCase().includes(filterValue)
    );
  }

  displayEmployee = (employeeId: any): string => {
    if (!this.employeeList) return '';
    const found = this.employeeList.find(e => e.id === employeeId || e.id == employeeId);
    if (!found) return '';
    return `${found.f_name || ''} ${found.m_name || ''} ${found.l_name || ''}`.replace(/\s+/g, ' ').trim();
  }

  onEmployeeSelected(event: any) {
    const selectedId = event.option.value;
    this.editForm.get('employee_id')?.setValue(selectedId);
  }

  clearEmployee() {
    this.editForm.get('employee_id')?.setValue(null);
  }

  setupUserTypeLogic() {
    const userTypeGroup = this.editForm.get('user_type') as FormGroup;

    userTypeGroup.get('areaManager')?.valueChanges.subscribe(isChecked => {
      if (isChecked) {
        userTypeGroup.patchValue({ hq: false, shop: false }, { emitEvent: false });
      }
    });

    userTypeGroup.get('hq')?.valueChanges.subscribe(isChecked => {
      if (isChecked) {
        userTypeGroup.patchValue({ areaManager: false }, { emitEvent: false });
      }
    });

    userTypeGroup.get('shop')?.valueChanges.subscribe(isChecked => {
      if (isChecked) {
        userTypeGroup.patchValue({ areaManager: false }, { emitEvent: false });
      }
    });
  }

  updateCardNoFromEmployee(employeeId: any) {
    const cardNoControl = this.editForm.get('card_no');
    if (employeeId) {
      if (this.employeeList && this.employeeList.length > 0) {
        const emp = this.employeeList.find(e => e.id === employeeId);
        if (emp && emp.card_no) {
          cardNoControl?.setValue(emp.card_no, { emitEvent: false });
        }
      }
      cardNoControl?.disable({ emitEvent: false });
    } else {
      cardNoControl?.enable({ emitEvent: false });
    }
  }

  get showMenuCheckboxes() {
    return this.editForm.get('user_type.shop')?.value;
  }

  get isShopUser(): boolean {
    const currentUser = this.permissionService.getCurrentUser();
    // If logged in as SHOP, hide the SHOPS mapping tab (it's only for HQ/Area Managers)
    if (currentUser?.user_type === 'SHOP') {
      return false;
    }

    const userGroupId = this.editForm.get('user_group_id')?.value;
    if (!userGroupId) return false;
    const group = this.userGroupList.find((g: any) => g.id == userGroupId);
    if (!group) return false;
    // 1: Shop, 2: HQ & Shop, 3: Area Manager. Only 0 is exclusively HQ.
    return group.user_type === 1 || group.user_type === 2;
  }

  getUserTypeValue(): number {
    const ut = this.editForm.get('user_type')?.value;
    if (ut.areaManager) return 3;
    if (ut.hq && ut.shop) return 2;
    if (ut.hq) return 0;
    if (ut.shop) return 1;
    return -1;
  }

  get isEditMode(): boolean {
    return !!(this.userInfo && this.userInfo.id && this.userInfo.id !== 0);
  }

  /**
   * Dirtyness check
   * @returns 
   */
  isDirty(): boolean {
    return this.isFormDirty;
  }

  /**
   * checks the form is edited
   */
  navBack(): void {

    this.router.navigate(['/user/user']);

  }

  /**
   * If tas is is null set an empty area info
   * if area id is not null call api to rtrive the info
   * @param userGroupId 
   */
  setUserInfo(userGroupId: any): void {

    let response = this.userService.getById(userGroupId).subscribe({
      next: (response) => {
        if (response.status == 'SUCCESS') {
          this.userInfo = response.data.users;
          // const menu = response.data.user_group.menu;

          // SET USER TYPE

          const userTypeGroup = this.editForm.get('user_type') as FormGroup;
          if (this.userInfo.employee_id) {
            this.userInfo.employee_id = Number(this.userInfo.employee_id);
          }
          if (this.userInfo.user_group_id) {
            this.userInfo.user_group_id = Number(this.userInfo.user_group_id);
          }
          // Set the flag BEFORE patching the form to prevent duplicate API call
          this.shopUserMappingLoadedFromSetUserGroupInfo = true;
          // Ensure is_active is boolean for the form, but keep as number in the model
          const { password, ...userInfoWithoutPassword } = this.userInfo;
          const patchObj = { ...userInfoWithoutPassword, is_active: this.toBoolean(this.userInfo.is_active) };
          this.editForm.patchValue(patchObj);
          if (this.userInfo.user_type == 3) {
            userTypeGroup.patchValue({ hq: false, shop: false, areaManager: true });
          } else if (this.userInfo.user_type == 2) {
            userTypeGroup.patchValue({ hq: true, shop: true, areaManager: false });
          } else if (this.userInfo.user_type == 1) {
            userTypeGroup.patchValue({ hq: false, shop: true, areaManager: false });
          } else {
            userTypeGroup.patchValue({ hq: true, shop: false, areaManager: false });
          }

          // const menuGroup = this.editForm.get('menu') as FormGroup;
          // menuGroup.patchValue({
          //   is_dashboard_page_visible: menu?.is_dashboard_page_visible,
          //   is_people_page_visible: menu?.is_people_page_visible,
          //   is_utilities_page_visible: menu?.is_utilities_page_visible,
          //   is_reports_page_visible: menu?.is_reports_page_visible,
          //   is_settings_page_visible: menu?.is_settings_page_visible,
          //   is_manager_page_visible: menu?.is_manager_page_visible,
          //   is_user_page_visible:  menu?.is_user_page_visible,
          //   is_userdefined_page1_visible: menu?.is_userdefined_page1_visible,
          //   is_userdefined_page2_visible: menu?.is_userdefined_page2_visible,
          //   is_userdefined_page3_visible: menu?.is_userdefined_page3_visible,
          //   is_attendance_page_visible: menu?.is_attendance_page_visible
          // });

          // Call shop-user-mapping API
          if (this.userInfo.id !== undefined && this.userInfo.id !== null) {
            this.userService.getShopUserMapping(Number(this.userInfo.id)).subscribe(res => {
              this.zone.run(() => {
                if (res.status === 'SUCCESS') {
                  this.shopUserMapping = [...res.data];
                  // Extract unique area/city list
                  this.areaList = Array.from(new Set(this.shopUserMapping.map(shop => shop.area || 'No Area'))).filter(Boolean);
                  // By default, select all areas
                  this.selectedAreas = [...this.areaList];
                  this.sortedShopUserMapping = this.getFilteredAndSortedShopUserMapping();
                } else {
                  this.shopUserMapping = [];
                  this.areaList = [];
                  this.selectedAreas = [];
                  this.sortedShopUserMapping = [];
                }
                // this.shopUserMappingLoadedFromSetUserGroupInfo = true; // already set above
                console.log('shopUserMapping after setUserInfo:', this.shopUserMapping);
                this.cdr.detectChanges();
              });
            });
          }

          this.title = this.getTitle();
          this.isFormDirty = false;
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
        }
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to get data from server. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        }
        this.snackBarService.openSnackBar(snackBarData);
      }
    });
  }

  /**
   * 
   * @returns  the title for the dialog
   * sine the same dialog is used for edit and new, dynamically decide the title
   */
  getTitle(): string {
    let title = (this.userInfo.id == 0 || !this.userInfo.id) ? "New User" : "Editing : " + (this.userInfo.name || '');
    return title;
  }

  /**
     * On save
     * if the details are valid call the api to update the changes.
     */
  save(): void {
    if (this.editForm.valid) {
      const formRaw = this.editForm.getRawValue();
      const { password: formPassword, confirm_password: formConfirm, ...otherFormData } = formRaw;
      Object.assign(this.userInfo, otherFormData);

      // Preserve existing password if form fields are empty (Edit Mode)
      // Only set password from the form if the user actually typed one (New Mode)
      if (this.isEditMode) {
        // If it's edit mode, we only use this.userInfo.password if it was changed by the dialog 
        // (the dialog already sets this.userInfo.password to hashed version)
        // If the form has a password (rare for edit unless we changed it), use it.
        if (formPassword) {
          this.userInfo.password = CryptoJS.MD5(formPassword).toString();
        }
        // else: leave this.userInfo.password as it is (it contains the value from DB or the Change Password dialog)
      } else {
        // New Mode: Always hash what's in the form
        if (formPassword) {
          this.userInfo.password = CryptoJS.MD5(formPassword).toString();
        }
      }

      // Convert is_active boolean to number for the model
      this.userInfo.is_active = formRaw.is_active ? 1 : 0;
      this.userInfo.user_type = this.getUserTypeValue();

      // Prepare full shops data for payload
      const shopsPayload = this.shopUserMapping.map(shop => ({
        ...shop,
        is_exists: (shop.is_exists == '1' || shop.is_exists === 1 || shop.is_exists === true) ? '1' : '0'
      }));
      this.userInfo.shops = shopsPayload;

      // MD5 Hashing safety check (if not already handled above)
      if (this.userInfo.password && this.userInfo.password.length > 0) {
        const isAlreadyMD5 = /^[a-f0-9]{32}$/i.test(this.userInfo.password);
        if (!isAlreadyMD5) {
          this.userInfo.password = CryptoJS.MD5(this.userInfo.password).toString();
        }
      }

      this.userService.update(this.userInfo).subscribe({
        next: (response) => {
          if (response.status == 'SUCCESS') {
            // handle success
            this.isFormDirty = false;
            let snckBarData: SnackBarData = {
              type: 'success',
              title: 'Saved',
              message: 'The record for ' + this.userInfo.name + " has been updated succesfully."
            }
            this.snackBarService.openSnackBar(snckBarData);
            this.router.navigate(['/user/user']);
          } else if (response.code === 'DATA_CHANGED_AT_SERVER') {
            let snackBarData: SnackBarData = {
              message: 'This record has been updated by another user. Please reload the data to see the latest changes.',
              title: 'Conflict Detected',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
            // Refresh the data to show the latest version from server
            if (this.userInfo.id) {
              this.setUserInfo(this.userInfo.id);
            }
          } else {
            let snackBarData: SnackBarData = {
              message: response.message,
              title: 'Failed!!!',
              type: 'error'
            }
            this.snackBarService.openSnackBar(snackBarData);
          }
        },
        error: (error) => {
          // handle error
          let snackBarData: SnackBarData = {
            message: 'Failed to update. Connection to the server failed',
            title: 'Failed!!!',
            type: 'error'
          }
          this.snackBarService.openSnackBar(snackBarData);
        }
      });
    } else {
      this.editForm.markAllAsTouched();
      console.error('Form is invalid. Current errors:', this.editForm.errors);
      // Log individual control errors for precise debugging
      Object.keys(this.editForm.controls).forEach(key => {
        const controlErrors = this.editForm.get(key)?.errors;
        if (controlErrors != null) {
          console.error('Key:', key, 'Errors:', controlErrors);
        }
      });
    }
  }


  loadUserGroupData(): void {

    let requestData = { "search": { "filters": { "simpleSearch": [], "advSearch": [], "scope": { "limit": 1000, "offset": 0 } }, "sort": [{ "column": "name", "order": "asc" }] } }
    let response = this.userGroupService.getList(requestData).subscribe(response => {
      if (response.status == 'SUCCESS') {
        this.userGroupList = response.data.user_groups;
        this.userGroupList$.next(this.userGroupList);

        const control = this.editForm.get('user_group_id');
        if (control && control.value !== null && control.value !== undefined) {
          control.setValue(control.value, { emitEvent: false });
        }
        this.cdr.detectChanges();
      }
    });

  }

  loadEmployeeData(): void {

    let requestData = { "search": { "filters": { "simpleSearch": [], "advSearch": [], "scope": { "limit": 1000, "offset": 0 } }, "sort": [{ "column": "f_name", "order": "asc" }] } }
    let response = this.employeeService.getEmployeeList(requestData).subscribe(response => {
      if (response.status == 'SUCCESS') {
        //this.categoryList = response.data.categories;
        this.employeeList = response.data.employees;
        this.employeeList$.next(this.employeeList);
        // After loading employee list, synchronize the card no if an employee is currently selected
        this.updateCardNoFromEmployee(this.editForm.get('employee_id')?.value);

        const control = this.editForm.get('employee_id');
        if (control && control.value !== null && control.value !== undefined) {
          control.setValue(control.value, { emitEvent: false });
        }
        this.cdr.detectChanges();
      }
    });

  }

  openChangePasswordDialog(): void {
    const dialogRef = this.dialog.open(ChangePasswordDialogComponent, {
      width: '350px',
      data: {}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.newPassword) {
        // Set the new password in the payload (will be hashed in save() or here)
        // Let's hash it immediately for safety
        this.userInfo.password = CryptoJS.MD5(result.newPassword).toString();

        // Mark form as dirty so user knows they have a pending change
        this.isFormDirty = true;

        this.snackBarService.openSnackBar({
          type: 'success',
          title: 'Password Set',
          message: 'Password updated. Please click SAVE to apply changes.'
        });
      }
    });
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password')?.value;
    const confirm = form.get('confirm_password')?.value;
    const confirmControl = form.get('confirm_password');

    // Only validate if there is content in either field
    if (password || confirm) {
      if (password !== confirm) {
        confirmControl?.setErrors({ ...confirmControl.errors, passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        // They match - remove the error
        if (confirmControl?.hasError('passwordMismatch')) {
          const errors = { ...confirmControl.errors };
          delete errors['passwordMismatch'];
          confirmControl.setErrors(Object.keys(errors).length ? errors : null);
        }
      }
    } else {
      // Both are empty - clear any previous mismatch error
      if (confirmControl?.hasError('passwordMismatch')) {
        const errors = { ...confirmControl.errors };
        delete errors['passwordMismatch'];
        confirmControl.setErrors(Object.keys(errors).length ? errors : null);
      }
    }
    return null;
  }

  dateRangeValidator(form: FormGroup) {
    const from = form.get('valid_from')?.value;
    const to = form.get('valid_to')?.value;
    if (from && to) {
      const fromDate = new Date(from);
      const toDate = new Date(to);
      if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime()) && fromDate > toDate) {
        form.get('valid_from')?.setErrors({ dateRange: true });
        form.get('valid_to')?.setErrors({ dateRange: true });
        return { dateRange: true };
      } else {
        if (form.get('valid_from')?.hasError('dateRange')) {
          form.get('valid_from')?.setErrors(null);
        }
        if (form.get('valid_to')?.hasError('dateRange')) {
          form.get('valid_to')?.setErrors(null);
        }
      }
    } else {
      if (form.get('valid_from')?.hasError('dateRange')) {
        form.get('valid_from')?.setErrors(null);
      }
      if (form.get('valid_to')?.hasError('dateRange')) {
        form.get('valid_to')?.setErrors(null);
      }
    }
    return null;
  }

  getSortedShopUserMapping(data: any[]): any[] {
    return [...data].sort((a, b) => {
      if (a.code < b.code) return 1;
      if (a.code > b.code) return -1;
      return b.name.localeCompare(a.name);
    });
  }

  trackByShop(index: number, shop: any) {
    return shop.id || shop.code;
  }

  // Add this helper method to the class:
  toBoolean(val: any): boolean {
    if (val === undefined || val === null) return true;
    return val === true || val === 1 || val === '1';
  }

  isShopChecked(shop: any): boolean {
    return String(shop.is_exists) === '1';
  }

  onShopCheckboxChange(shop: any, checked: boolean) {
    // Find index and replace with new object (immutability)
    const idx = this.shopUserMapping.findIndex(s => (s.id || s.code) === (shop.id || shop.code));
    if (idx > -1) {
      this.shopUserMapping = [
        ...this.shopUserMapping.slice(0, idx),
        { ...shop, is_exists: checked ? '1' : '0' },
        ...this.shopUserMapping.slice(idx + 1)
      ];
      // Use the filtering method to preserve current filters
      this.sortedShopUserMapping = this.getFilteredAndSortedShopUserMapping();
      this.cdr.detectChanges();
    }
  }

  // Helper to get filtered and sorted shops based on selected areas
  getFilteredAndSortedShopUserMapping(): any[] {
    let filtered = this.shopUserMapping;
    if (this.selectedAreas && this.selectedAreas.length > 0) {
      filtered = filtered.filter(shop => this.selectedAreas.includes(shop.area || 'No Area'));
    }
    if (this.shopSearch && this.shopSearch.trim() !== '') {
      const search = this.shopSearch.trim().toLowerCase();
      filtered = filtered.filter(shop =>
        (shop.code && shop.code.toLowerCase().includes(search)) ||
        (shop.name && shop.name.toLowerCase().includes(search)) ||
        ((shop.area || 'No Area').toLowerCase().includes(search))
      );
    }
    return this.getSortedShopUserMapping(filtered);
  }

  onShopSearchChange(search: string) {
    this.shopSearch = search;
    this.sortedShopUserMapping = this.getFilteredAndSortedShopUserMapping();
    this.cdr.detectChanges();
  }

  isAllShopsChecked(): boolean {
    return this.sortedShopUserMapping.length > 0 &&
      this.sortedShopUserMapping.every(shop => this.isShopChecked(shop));
  }

  isSomeShopsChecked(): boolean {
    return this.sortedShopUserMapping.some(shop => this.isShopChecked(shop)) &&
      !this.isAllShopsChecked();
  }

  onSelectAllShops(checked: boolean) {
    const updated = this.shopUserMapping.map(shop => {
      // Only update shops in the current filtered list
      if (this.sortedShopUserMapping.some(s => (s.id || s.code) === (shop.id || shop.code))) {
        return { ...shop, is_exists: checked ? '1' : '0' };
      }
      return shop;
    });
    this.shopUserMapping = updated;
    this.sortedShopUserMapping = this.getFilteredAndSortedShopUserMapping();
    this.cdr.detectChanges();
  }
  // Call this when area selection changes from the dropdown
  onAreaSelectionChange(selected: string[]) {
    this.selectedAreas = selected;
    this.sortedShopUserMapping = this.getFilteredAndSortedShopUserMapping();
    this.cdr.detectChanges();
  }
}

// Dialog component for password change
@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule
  ],
  template: `
      <h2 mat-dialog-title>Change Password</h2>
      <mat-dialog-content>
        <form [formGroup]="passwordForm">
          <mat-form-field style="width:100%">
            <mat-label>New Password</mat-label>
            <input matInput type="password" formControlName="newPassword">
            <mat-error *ngIf="passwordForm.controls['newPassword'].hasError('required')">Required</mat-error>
          </mat-form-field>
          <mat-form-field style="width:100%">
            <mat-label>Confirm Password</mat-label>
            <input matInput type="password" formControlName="confirmPassword">
            <mat-error *ngIf="passwordForm.controls['confirmPassword'].hasError('required')">Required</mat-error>
            <mat-error *ngIf="passwordForm.hasError('passwordMismatch')">Passwords do not match</mat-error>
          </mat-form-field>
        </form>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button (click)="onCancel()">Cancel</button>
        <button mat-button color="primary" (click)="onSave()" [disabled]="passwordForm.invalid">Save</button>
      </mat-dialog-actions>
    `
})
export class ChangePasswordDialogComponent {
  passwordForm: FormGroup;
  constructor(
    public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.passwordForm = this.fb.group({
      newPassword: ['', Validators.required],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }
  passwordMatchValidator(form: FormGroup) {
    return form.get('newPassword')!.value === form.get('confirmPassword')!.value ? null : { passwordMismatch: true };
  }
  onCancel(): void {
    this.dialogRef.close();
  }
  onSave(): void {
    if (this.passwordForm.valid) {
      this.dialogRef.close({ newPassword: this.passwordForm.value.newPassword });
    }
  }
}